import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import EmptyState from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle, Bell, CheckCircle2, Thermometer,
  SprayCan, FileText, Info, AlertCircle,
} from "lucide-react";
import moment from "moment";

const TYPE_CONFIG = {
  temperature: { label: "Temperatura", icon: Thermometer, color: "text-blue-600", bg: "bg-blue-100" },
  cleaning: { label: "Neteja", icon: SprayCan, color: "text-green-600", bg: "bg-green-100" },
  incident: { label: "Incidència", icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-100" },
  document_expiry: { label: "Document", icon: FileText, color: "text-purple-600", bg: "bg-purple-100" },
  system: { label: "Sistema", icon: Info, color: "text-gray-600", bg: "bg-gray-100" },
};

const SEV_CONFIG = {
  info: { label: "Info", class: "bg-blue-100 text-blue-700 border-blue-200" },
  warning: { label: "Avís", class: "bg-amber-100 text-amber-700 border-amber-200" },
  danger: { label: "Perill", class: "bg-orange-100 text-orange-700 border-orange-200" },
  critical: { label: "Crític", class: "bg-red-100 text-red-700 border-red-200" },
};

const STATUS_LEFT = {
  active: "border-l-red-500",
  acknowledged: "border-l-amber-400",
  resolved: "border-l-gray-200",
};

export default function Alerts() {
  const { currentVenue, user, loading: venueLoading } = useVenue();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [resolving, setResolving] = useState(null);

  useEffect(() => {
    if (currentVenue) loadAlerts();
  }, [currentVenue]);

  const loadAlerts = async () => {
    setLoading(true);
    const items = await hosys.entities.Alert.filter(
      { venue_id: currentVenue.id }, "-created_date"
    );
    setAlerts(items);
    setLoading(false);
  };

  const handleAcknowledge = async (alert) => {
    setResolving(alert.id);
    await hosys.entities.Alert.update(alert.id, { status: "acknowledged" });
    setAlerts(prev => prev.map(a => a.id === alert.id ? { ...a, status: "acknowledged" } : a));
    setResolving(null);
  };

  const handleResolve = async (alert) => {
    setResolving(alert.id);
    await hosys.entities.Alert.update(alert.id, {
      status: "resolved",
      resolved_by_name: user.full_name,
      resolved_date: new Date().toISOString(),
    });
    setAlerts(prev => prev.map(a => a.id === alert.id ? { ...a, status: "resolved" } : a));
    setResolving(null);
  };

  if (venueLoading) return <QuickLoader />;

  const filtered = filter === "all" ? alerts : alerts.filter(a => a.status === filter);
  const activeCount = alerts.filter(a => a.status === "active").length;
  const criticalCount = alerts.filter(a => a.status === "active" && (a.severity === "critical" || a.severity === "danger")).length;

  return (
    <div className="space-y-5 pb-6">
      <PageHeader
        title="Centre d'Alertes"
        subtitle={activeCount > 0 ? `⚠ ${activeCount} alertes actives${criticalCount > 0 ? ` · ${criticalCount} crítiques` : ""}` : "Tot en ordre"}
      />

      {/* Critical banner */}
      {criticalCount > 0 && (
        <div className="bg-red-600 text-white rounded-2xl p-4 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 flex-shrink-0" />
          <div>
            <p className="font-bold">{criticalCount} alerta(es) crítiques actives</p>
            <p className="text-red-100 text-sm">Cal atenció immediata</p>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: "active", label: `Actives (${activeCount})` },
          { key: "acknowledged", label: `Vistes (${alerts.filter(a => a.status === "acknowledged").length})` },
          { key: "resolved", label: `Resoltes (${alerts.filter(a => a.status === "resolved").length})` },
          { key: "all", label: `Totes (${alerts.length})` },
        ].map(({ key, label }) => (
          <Button
            key={key}
            variant={filter === key ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(key)}
            className={filter === key ? "bg-[#7B2D3F] hover:bg-[#5B1D2F]" : ""}
          >
            {label}
          </Button>
        ))}
      </div>

      {/* List */}
      {loading ? <QuickLoader /> : filtered.length === 0 ? (
        <EmptyState
          icon={Bell}
          title={filter === "active" ? "Cap alerta activa" : "Sense alertes"}
          description={filter === "active" ? "No hi ha alertes actives en aquest moment. Bon treball! 🎉" : "No hi ha alertes amb aquest filtre."}
        />
      ) : (
        <div className="space-y-3">
          {filtered.map(alert => {
            const tc = TYPE_CONFIG[alert.type] || TYPE_CONFIG.system;
            const sc = SEV_CONFIG[alert.severity] || SEV_CONFIG.warning;
            const Icon = tc.icon;
            return (
              <Card key={alert.id} className={`p-4 border-0 shadow-sm border-l-4 ${STATUS_LEFT[alert.status] || "border-l-gray-200"} ${alert.status === "resolved" ? "opacity-60" : ""}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${tc.bg}`}>
                    <Icon className={`w-5 h-5 ${tc.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${sc.class}`}>
                        {sc.label}
                      </span>
                      <Badge variant="secondary" className="border-0 text-[10px]">{tc.label}</Badge>
                      {alert.status === "resolved" && (
                        <span className="text-[10px] text-emerald-600 font-medium flex items-center gap-0.5">
                          <CheckCircle2 className="w-3 h-3" /> Resolt
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">{alert.title}</h3>
                    {alert.message && <p className="text-xs text-gray-500 mt-0.5">{alert.message}</p>}
                    <p className="text-xs text-gray-400 mt-1">{moment(alert.created_date).format("DD/MM/YYYY HH:mm")}</p>
                    {alert.resolved_by_name && (
                      <p className="text-xs text-emerald-600 mt-0.5">Resolt per {alert.resolved_by_name}</p>
                    )}
                  </div>
                  {alert.status === "active" && (
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => handleAcknowledge(alert)} disabled={resolving === alert.id} className="text-xs">
                        Vista
                      </Button>
                      <Button size="sm" onClick={() => handleResolve(alert)} disabled={resolving === alert.id} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] text-xs">
                        Resoldre
                      </Button>
                    </div>
                  )}
                  {alert.status === "acknowledged" && (
                    <Button size="sm" onClick={() => handleResolve(alert)} disabled={resolving === alert.id} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] text-xs flex-shrink-0">
                      Tancar
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}